import merge from 'lodash/merge'
import reduce from 'lodash/reduce'
import { isDarkColor } from '@signal/utils/isDarkColor'
import { SIGNAL_FONT_VARIANT } from './constants'
import {
  colorBlackSelector,
  getColorTextSelector,
  colorWhiteSelector
} from '../selectors'

/**
 * Combines a list of theme middleware functions into a single "middleware"
 * function that passes custom overrides through each of the middleware functions
 * provided before merging the resulting {@link ThemeOverrides} object with the
 * base theme overrides.
 * @param {Array.<ThemeMiddleware>} arrThemeMiddleware - ordered list of theme middleware
 * @return {Function}
 */
export const combineThemeMiddleware = arrThemeMiddleware => {
  /**
   * Passes the supplied {@link ThemeOverrides} object through a series of
   * {@link ThemeMiddleware} functions before merging the final result into the
   * base overrides object.
   * @param {ThemeOverrides} baseOverrides - base theme overrides
   * @param {ThemeOverrides} customOverrides - custom theme overrides
   * @returns {ThemeOverrides}
   * @global
   */
  return function combinedThemeMiddleware(baseOverrides, customOverrides) {
    const resultOverrides = reduce(
      arrThemeMiddleware,
      // Each middleware function is called with the base theme overrides and
      // the overrides object returned from the previous middleware
      (prevCustomOverrides, middleware) =>
        middleware(baseOverrides, prevCustomOverrides),
      // The custom overrides object provided the function is used as the
      // initial value for  `reduce()`
      customOverrides
    )
    // Merge the resulting overrides object that has passed through each of the
    // middleware functions into the base overrides object
    return merge({}, baseOverrides, resultOverrides)
  }
}

/**
 * @param {String} maybeSignalFontVariantKey
 * @return {Boolean}
 */
export function isSignalFontVariant(maybeSignalFontVariantKey) {
  return maybeSignalFontVariantKey.startsWith(SIGNAL_FONT_VARIANT)
}

/**
 * Create and return a prefixed name for a custom font variant
 * @param {String} name
 * @return {String} name plus the prefix constant
 */
export function getSignalFontVariantName(name) {
  return `${SIGNAL_FONT_VARIANT}${name}`
}

/**
 * get an appropriate text color to use for the given background
 * @param {MUIThemeType} - theme - the MUI theme object
 * @param {string} - backgroundColor - the background color
 * @param {number} - threshold - a threshold value to use for determing "dark".  Defaults to 128 (midpoint)
 * @return {string} - the text color to use
 */
export function getTextColorForBackground(
  theme,
  backgroundColor,
  threshold = 128
) {
  const textColor = getColorTextSelector()(theme)
  const darkColor = isDarkColor(textColor, 64)
    ? textColor
    : colorBlackSelector(theme)
  const lightColor = !isDarkColor(textColor, 192)
    ? textColor
    : colorWhiteSelector(theme)
  return isDarkColor(backgroundColor, threshold) ? lightColor : darkColor
}
